import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import React, { useEffect } from 'react'
import { getCookie } from '../Utils/CookieHelper';
import Service from '../Networking/NetworkingUtils';
import { useSnackbar } from 'notistack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import UploadIcon from '@mui/icons-material/Upload';
import { blobSAS, blobWriteString, generateRandomString } from '../Helper/BlobHelper';
import { useDropzone } from 'react-dropzone';


const token = getCookie("bb_metaverse_token");

const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out'
};

const MAX_IMAGE_UPLOAD_SIZE = 300 * 1024;


const Client = () => {

    const [tableData, setTableData] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [isEdit, setIsEdit] = React.useState(false);
    const [formData, setFormData] = React.useState({ name: '' });
    const [selectedRow, setSelectedRow] = React.useState(null);
    const [logoUploadDialog, setLogoUploadDialog] = React.useState(false);
    const [progress, setProgress] = React.useState(0);
    const [uploadLoading, setUploadLoading] = React.useState(false);
    const [projectLogoSasToken, setProjectLogoSasToken] = React.useState(null);
    const [searchText, setSearchText] = React.useState('');
    const { acceptedFiles, fileRejections, getRootProps, getInputProps } = useDropzone({
        maxFiles: 1,
        maxSize: MAX_IMAGE_UPLOAD_SIZE,
        accept: {
            'image/*': []
        },
    });
    const { enqueueSnackbar } = useSnackbar();

    const getClientLogoUrl = (logo) => {
        if (!logo) {
            return ''
        }
        if (logo.startsWith('http://') || logo.startsWith('https://')) {
            try {
                const url = new URL(logo)
                return url.protocol === 'http:' || url.protocol === 'https:' ? logo : ''
            } catch (error) {
                return ''
            }
        }
        if (!logo.startsWith('client-info/')) {
            return ''
        }
        return projectLogoSasToken ? projectLogoSasToken.fd_account_url + '/' + logo + "?" + projectLogoSasToken?.read_only?.['client-info'].sas_token : ''
    }

    const renderClientLogo = (row) => {
        const logoUrl = getClientLogoUrl(row.logo)

        return logoUrl ?
            <Box
                component="img"
                src={logoUrl}
                alt={row.name || 'Client logo'}
                sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 1,
                    objectFit: 'cover',
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            /> : null
    }

    const columns = [
        { title: 'Client Name', field: 'name' },
        { title: 'Logo', field: 'logo', render: renderClientLogo }
    ]

    const normalizedSearchText = searchText.trim().toLowerCase()
    const filteredTableData = normalizedSearchText ? tableData.filter((row) => (
        columns.some((column) => String(row[column.field] || '').toLowerCase().includes(normalizedSearchText))
    )) : tableData

    const files = acceptedFiles.map(file => (
        <li key={file.path}>
            {file.path} - {file.size} bytes
        </li>
    ));

    const rejectedFiles = fileRejections.map(({ file, errors }) => (
        <li key={file.path}>
            {file.path} - {errors.map(error => error.message).join(', ')}
        </li>
    ));




    const getData = () => {
        setLoading(true)
        Service.get(`/api/clients/get_client_details/`, {
            headers: {
                Authorization: "Token " + token,
            },
        })
            .then(res => {
                setTableData(res.data)
                setLoading(false)
            })
            .catch(error => {
                console.log(error)
                enqueueSnackbar('Something went wrong!', {
                    variant: 'error'
                });
                setLoading(false)
            });
    }

    const getProjectLogoSasToken = async () => {
        const tokenData = await blobSAS({ token_type: 'read_only', container_name: 'client-info' });
        setProjectLogoSasToken(tokenData);
    }

    const handleDialogClose = () => {
        setDialogOpen(false)
        setIsEdit(false)
        setFormData({ name: '' })
    }

    const handleAddClick = () => {
        setFormData({ name: '' })
        setIsEdit(false)
        setDialogOpen(true)
    }

    const handleEditClick = (row) => {
        setFormData({ ...row, name: row.name || '' })
        setIsEdit(true)
        setDialogOpen(true)
    }

    const addClient = (name) => {
        Service.post('/api/clients/create_client_details/', {
            headers: {
                Authorization: "Token " + token,
            },
            data: { name }
        })
            .then(res => {
                handleDialogClose()
                getData()
                enqueueSnackbar('Client added Successfully', {
                    variant: 'success'
                });
            })
            .catch(error => {
                console.log(error)
                enqueueSnackbar('Something went wrong!', {
                    variant: 'error'
                });
            });
    }

    const editClient = (name) => {
        Service.patch('/api/clients/edit_client_details/', {
            headers: {
                Authorization: "Token " + token,
            },
            data: { ...formData, name }
        })
            .then(res => {
                handleDialogClose()
                getData()
                enqueueSnackbar('Client edited Successfully', {
                    variant: 'success'
                });
            })
            .catch(error => {
                console.log(error)
                enqueueSnackbar('Something went wrong!', {
                    variant: 'error'
                });
            });
    }

    const submitClient = () => {
        const name = formData.name.trim()

        if (!name) {
            enqueueSnackbar('Name is required!', {
                variant: 'warning'
            });
            return
        }

        if (isEdit) {
            editClient(name)
        } else {
            addClient(name)
        }
    }


    useEffect(() => {
        getData()
        getProjectLogoSasToken()
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const openUploadDialog = (row) => {
        setSelectedRow(row)
        setProgress(0)
        setLogoUploadDialog(true)
    }

    const handleUploadDialog = () => {
        setSelectedRow(null)
        setLogoUploadDialog(false)
    }

    const updateClientLogo = (filePath, rowData) => {
        Service.patch('/api/clients/edit_client_details/', {
            headers: {
                Authorization: "Token " + token,
            },
            data: { ...rowData, logo: filePath }
        })
            .then(res => {
                enqueueSnackbar('Logo added successfully!', {
                    variant: 'success'
                });
                setSelectedRow(null)
                setLogoUploadDialog(false)
                getData()
            })
            .catch(error => {
                console.log(error)
                enqueueSnackbar('Something went wrong!', {
                    variant: 'error'
                });
            });
    }

    const uploadFiles = async () => {
        const { BlobServiceClient } = require("@azure/storage-blob");
        window.Buffer = require("buffer").Buffer;

        try {
            if (acceptedFiles.length === 0) {
                enqueueSnackbar('Please select an image up to 300KB.', {
                    variant: 'warning'
                });
                return;
            }

            setUploadLoading(true)
            const blobSasUrl = await blobWriteString({ token_type: 'write_only', container_name: 'client-info' });
            const blobServiceClient = new BlobServiceClient(blobSasUrl);
            const containerName = "client-info/BBVCLIENT" + selectedRow.id + "/Logos";
            const containerClient = blobServiceClient.getContainerClient(containerName);
            const file = acceptedFiles[0];
            const fileExtension = file.name.split('.').pop();
            const fileName = generateRandomString() + "." + fileExtension;
            const blockBlobClient = containerClient.getBlockBlobClient(fileName);

            await blockBlobClient.uploadData(file, {
                onProgress: ev => {
                    setProgress((ev.loadedBytes / file.size) * 100)
                }
            });
            setUploadLoading(false)
            updateClientLogo(containerName + "/" + fileName, selectedRow)
        }
        catch (error) {
            setUploadLoading(false)
            console.log(error.message);
            enqueueSnackbar('Something went wrong!', {
                variant: 'error'
            });
        }
    }




    return (
        <div> {loading ? <Box display='flex' justifyContent={'center'} alignItems={'center'} width='100%'><CircularProgress /></Box> :
            <div>
                <TableContainer component={Paper}>
                    <Box width='100%' display='flex' justifyContent='space-between' alignItems='center' gap={2} padding='14px 16px 10px'>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                fontSize: 20,
                                color: 'text.primary',
                            }}
                        >
                            Client
                        </Typography>
                        <Box display='flex' alignItems='center' gap={1}>
                            <TextField
                                size="small"
                                placeholder="Search clients"
                                value={searchText}
                                onChange={(event) => setSearchText(event.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Tooltip title="Add"><span><IconButton onClick={handleAddClick}><AddIcon /></IconButton></span></Tooltip>
                        </Box>
                    </Box>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {columns && columns.map((column) => (
                                    <TableCell key={column.field}>{column.title}</TableCell>
                                ))}
                                <TableCell align='right'>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredTableData && filteredTableData.map((row) => (
                                <React.Fragment key={row.id}>
                                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        {columns && columns.map((column) => (
                                            <TableCell key={column.field}  >
                                                {column.render ? column.render(row) : column.lookup ? column.lookup[row[column.field]] : row[column.field]}
                                            </TableCell>
                                        ))}
                                        <TableCell sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end' }}>
                                            <Tooltip title="Edit"><span><IconButton onClick={() => handleEditClick(row)}><EditIcon /></IconButton></span></Tooltip>
                                            <Tooltip title="Upload Logo">
                                                <IconButton onClick={(event) => { event.stopPropagation(); openUploadDialog(row) }}>
                                                    <UploadIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                            {filteredTableData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length + 1} align="center">
                                        No clients found
                                    </TableCell>
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth='xs' fullWidth>
                    <DialogTitle>{isEdit ? 'Edit Client' : 'Add Client'}</DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Name"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDialogClose}>Cancel</Button>
                        <Button onClick={submitClient} variant="contained">{isEdit ? 'Save' : 'Add'}</Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    maxWidth="lg"
                    fullWidth={true}
                    open={logoUploadDialog}
                    onClose={handleUploadDialog}>
                    <DialogTitle>Upload Logo</DialogTitle>
                    <DialogContent>
                        <section className="container">
                            <div {...getRootProps({ style: baseStyle })}>
                                <input {...getInputProps()} />
                                <p>Drag 'n' drop an image here, or click to select one</p>
                                <Typography variant="caption">Maximum file size: 300KB</Typography>
                            </div>
                            <aside>
                                <h4>Files</h4>
                                <ul>{files}</ul>
                                {rejectedFiles.length > 0 ? <ul>{rejectedFiles}</ul> : null}
                            </aside>
                        </section>
                    </DialogContent>
                    <DialogActions>
                        <Button color='primary' variant='contained' onClick={() => handleUploadDialog()}>
                            close
                        </Button>
                        <Button disabled={acceptedFiles.length === 0 || uploadLoading} color='primary' variant='contained' onClick={() => uploadFiles()}>
                            submit
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog maxWidth="lg" fullWidth={true} open={uploadLoading}>
                    <DialogTitle marginTop="2px" spacing={2}>Uploading...</DialogTitle>
                    <DialogContent>
                        <Box display="flex" alignItems="center">
                            <Box width="100%" mr={1}>
                                <LinearProgress variant="determinate" value={progress} />
                            </Box>
                            <Box minWidth={35}>
                                <Typography variant="body2" color="primary">{`${Math.round(
                                    progress,
                                )}%`}</Typography>
                            </Box>
                        </Box>
                    </DialogContent>
                </Dialog>

            </div>
        }</div>
    )
}

export default Client